import { useState } from "react";
import { CalendarCheck, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";

export const ReadingDateRow = ({ workspace, reading, d, index, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(d.what || "Date from document");
  const [due, setDue] = useState(d.date);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/workspaces/${workspace.id}/deadlines`, {
        title, due_date: due, confirmed: true, document_id: reading.document_id, property_id: reading.property_id,
        from_reading_id: reading.id, reading_date_index: index, checked_against_document: true,
        notes: `Added from an AI reading after checking the document. Document wording: "${d.quote}"`.slice(0, 2000),
      });
      toast.success("Deadline saved."); setOpen(false); onAdded(index, data.deadline.id);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <li data-testid={`reading-date-${index}`} className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-navy">{formatDate(d.date)}</span>
        {d.deadline_id ? <span data-testid={`reading-date-added-${index}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800"><CalendarCheck className="h-3 w-3" /> Saved as a deadline</span>
          : <span data-testid={`reading-date-unconfirmed-${index}`} className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase text-amber-900">Unconfirmed</span>}
      </div>
      <p className="text-sm text-slate-700">{d.what}</p>
      <p className="border-l-2 border-gold pl-2 text-xs italic text-slate-500">"{d.quote}"</p>
      {!d.deadline_id && !open && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`reading-date-check-${index}`}><CalendarPlus className="mr-1.5 h-4 w-4" /> Check and add as deadline</Button>}
      {open && (
        <div className="space-y-3 rounded-md border bg-slate-50 p-3" data-testid={`reading-date-form-${index}`}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} data-testid={`reading-date-title-${index}`} className="h-10 bg-white" />
          <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid={`reading-date-input-${index}`} className="h-10 bg-white" />
          <label className="flex items-start gap-2 text-xs text-slate-700">
            <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid={`reading-date-checked-${index}`} className="mt-0.5" />
            I checked this date against the document myself and confirm it is correct.
          </label>
          {error && <p className="text-xs text-red-700" data-testid={`reading-date-error-${index}`}>{error}</p>}
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={!checked || !due || !title.trim() || busy} data-testid={`reading-date-save-${index}`} className="bg-navy hover:bg-navy-2">{busy ? "Saving..." : "Save deadline"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid={`reading-date-cancel-${index}`}>Cancel</Button>
          </div>
        </div>
      )}
    </li>
  );
};
